import {
	ChangeDetectionStrategy,
	Component,
	computed,
	inject,
	OnDestroy,
	OnInit,
	signal,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { CoreModule } from '@core/core.module';
import { dispatch, select } from '@ngxs/store';
import { ModalService } from '@ui/modal/modal.service';
import { UiModule } from '@ui/ui.module';
import { AVATAR_VARIANT_ORDER } from '@ui/utils/constants';
import { AtomicModule } from 'src/app/shared/atomic/atomic.module';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { ESigningRecipientListComponent } from '../../common/e-signing-recipient-list/e-signing-recipient-list.component';
import { EnvelopeService } from '@services/envelope/envelope.service';
import { ClientSigningDetailsViewModelComplete } from '@models/e-signing';
import { MODAL_DATA } from '@ui/modal/modal-data.token';
import { CommonModule } from '@angular/common';
import { IconsModule } from 'src/app/ui/icons/icons.module';
import { Observable } from 'rxjs';
import {
	CALL_TO_ACTION_TYPES,
	NOTE_TYPES,
} from 'src/app/ui/note/note.component';
import { UserProfileState } from 'src/app/shared/state/user-profile';
import { OverwriteFields } from 'src/app/shared/state/user-profile/actions';
import {
	createTrustLedgerPackageWarning,
	logTrustLedgerWarning,
} from '@state/matter/matter-document-packages-aggregator/trust-ledger-package-warning.utils';

@Component({
	selector: 'app-e-signing-envelope-creation-modal',
	imports: [
		UiModule,
		CommonModule,
		CoreModule,
		AtomicModule,
		ReactiveFormsModule,
		ESigningRecipientListComponent,
		IconsModule,
	],
	templateUrl: './e-signing-envelope-creation-modal.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ESigningEnvelopeCreationModalComponent
	implements OnInit, OnDestroy
{
	NOTE_TYPES = NOTE_TYPES;
	CALL_TO_ACTION_TYPES = CALL_TO_ACTION_TYPES;

	private modalService = inject(ModalService);
	private envelopeService = inject(EnvelopeService);
	private matterId_ = select(SelectedMatter.id);
	private modalData = inject(MODAL_DATA);
	private loadingBlurbs = [
		'Herding your documents like cats',
		'Persuading page 7 to play nice with page 2',
		'Aligning the stars for your documents',
		'Making sure your documents are ready for action',
		'Convincing pixels to sign on the dotted line',
		'Polishing your documents until they shine',
		'Preparing your envelope for its big moment',
		'Negotiating with stubborn margins',
		'Adding digital caffeine to speed things up',
		'Untangling legal jargon one word at a time',
		'Getting your documents in line for signing',
		'Adding digital postage stamps to your envelope',
	];
	private timerRef: NodeJS.Timeout;
	public variantOrderList = AVATAR_VARIANT_ORDER;
	public form: ReturnType<EnvelopeService['getRecipientForm']>['form'];
	public recipients: ClientSigningDetailsViewModelComplete[] = [];
	public title$: Observable<string>;

	public instructionTexts = [
		'Verify Email Addresses',
		'Add Signing Fields',
		'Schedule, Send, & Track',
	];
	public isSubmitting_ = signal(false);
	public hasSubmitted_ = signal(false);
	public percentage_ = signal(0);
	public activeLoadingBlurb_ = computed(() => {
		const total = this.loadingBlurbs.length;
		const percentage = this.percentage_();
		const index = Math.min(Math.floor((percentage / 100) * total), total - 1);
		return this.loadingBlurbs[index];
	});
	private OverwriteFields = dispatch(OverwriteFields);

	private packageId_ = computed(() => this.modalData.packageId);
	private tlWarning = createTrustLedgerPackageWarning(this.packageId_);

	ngOnInit() {
		const { form, recipients } = this.envelopeService.getRecipientForm();
		this.form = form;
		this.recipients = recipients;

		this.title$ = this.envelopeService.getSignersCount$(this.form);
		this.setFooterButtons();

		logTrustLedgerWarning(this.tlWarning);
	}

	ngOnDestroy() {
		if (this.timerRef) {
			clearInterval(this.timerRef);
		}

		if (!this.hideSigningFieldNote_()) {
			this.closeFieldNote();
		}
	}

	public hideSigningFieldNote_ = select(
		UserProfileState.isSignItESigningModalHidden,
	);

	public closeFieldNote(): void {
		this.OverwriteFields({
			field: 'settings.hideNotifications.hideESigningModalNote',
			value: true,
		});
	}

	public async onCreateEnvelope() {
		this.hasSubmitted_.set(true);
		if (this.form.invalid) return;
		this.isSubmitting_.set(true);

		try {
			this.modalService.clearPrimaryAction();
			this.modalService.clearSecondaryAction();
			this.startCounter();
			await Promise.all([
				this.envelopeService.saveRecipientsEmail(
					Object.entries(this.form.getRawValue()).map(
						([recipientId, { email, participantType }]) => ({
							recipientId,
							participantType,
							email,
						}),
					),
				),
				this.envelopeService.preparePackageForSigning(
					this.matterId_(),
					this.modalData.packageId,
					Object.entries(this.form.getRawValue()).reduce(
						(acc, [clientId, { isParticipating, participantType }]) =>
							isParticipating ? acc.concat({ clientId, participantType }) : acc,
						[],
					),
				),
			]);
		} catch {
			clearInterval(this.timerRef);
			this.isSubmitting_.set(false);
			this.percentage_.set(0);
			this.hasSubmitted_.set(false);
			this.setFooterButtons();
		}
	}

	private setFooterButtons() {
		this.modalService.setPrimaryAction('Prepare Document', () =>
			this.onCreateEnvelope(),
		);

		this.modalService.setSecondaryAction('Cancel', () =>
			this.modalService.closeModal(),
		);
	}

	private startCounter() {
		const interval = 125;
		let current = 0;

		const timer = setInterval(() => {
			current = (current + 1) % 301;
			this.percentage_.set(current);
		}, interval);

		this.timerRef = timer;
	}
}
